import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import ImageCropModal from '@/components/ImageCropModal';
import VerifiedBadge from '@/components/VerifiedBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { User, Mail, Phone, School, MapPin, Calendar as CalendarIcon, Upload, X } from 'lucide-react';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function Profile({ user, onLogout, onUpdateUser }) {
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    mobile_number: user.mobile_number || '',
    school_name: user.school_name || '',
    city: user.city || '',
    mentor: user.mentor || '',
    profile_photo: user.profile_photo || '',
    date_of_birth: user.date_of_birth || '',
    grade_year: user.grade_year || user.grade || '',
    school_type: user.school_type || ''
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Custom fields state
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [loadingCustomFields, setLoadingCustomFields] = useState(true);
  
  // Field locks state
  const [fieldLocks, setFieldLocks] = useState({});

  useEffect(() => {
    fetchCustomFields();
    fetchFieldLocks();
  }, []);

  const fetchCustomFields = async () => {
    try {
      console.log('Fetching custom fields and user data...');
      
      // CRITICAL: Fetch fresh user data from server to get latest custom_fields
      const userRes = await axios.get(`${API}/users/${user.id}`, { headers: getAuthHeaders() });
      const freshUserData = userRes.data;
      console.log('Fresh user data:', freshUserData);
      console.log('Custom fields from user:', freshUserData.custom_fields);
      
      // Fetch custom field definitions (only visible to students)
      const fieldsRes = await axios.get(`${API}/custom-fields`, { headers: getAuthHeaders() });
      setCustomFields(fieldsRes.data);
      console.log('Custom field definitions:', fieldsRes.data);
      
      // Initialize custom field values from fresh user data
      const userCustomFields = freshUserData.custom_fields || {};
      const initialValues = {};
      fieldsRes.data.forEach(field => {
        const fieldData = userCustomFields[field.id];
        let value = fieldData?.value || field.default_value || '';
        
        // For dropdown fields, ensure we never use empty string (causes Select component error)
        if (field.field_type === 'dropdown' && value === '') {
          value = undefined;
        }
        
        console.log(`Field ${field.label} (${field.id}): ${value}`);
        initialValues[field.id] = value;
      });
      setCustomFieldValues(initialValues);
      console.log('✓ Initialized custom field values:', initialValues);
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoadingCustomFields(false);
    }
  };

  const fetchFieldLocks = async () => {
    try {
      const res = await axios.get(`${API}/users/${user.id}/field-locks`, { headers: getAuthHeaders() });
      setFieldLocks(res.data.field_locks || {});
      console.log('Field locks:', res.data.field_locks);
    } catch (error) {
      console.error('Failed to fetch field locks:', error);
    }
  };

  // Helper to check if a field is locked by admin
  const isFieldLocked = (fieldName) => {
    return fieldLocks[fieldName] === true;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a .jpg or .png file');
      return;
    }

    // Validate file size (5 MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large — max 5 MB');
      return;
    }

    // Read file and show crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setImageFile(file);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async (croppedBlob) => {
    setShowCropModal(false);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', croppedBlob, imageFile.name);

      const res = await axios.post(`${API}/users/upload-profile-photo`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update form data
      const newPhotoUrl = res.data.photo_url;
      setFormData(prev => ({ ...prev, profile_photo: newPhotoUrl }));
      
      // Update user object in localStorage and state immediately
      const updatedUser = { ...user, profile_photo: newPhotoUrl };
      localStorage.setItem('lms_user', JSON.stringify(updatedUser));
      onUpdateUser(updatedUser);
      
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed — try again');
    } finally {
      setUploading(false);
      setImageSrc(null);
      setImageFile(null);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      await axios.delete(`${API}/users/profile-photo`, { headers: getAuthHeaders() });
      setFormData(prev => ({ ...prev, profile_photo: '' }));
      
      // Update user object in localStorage and state immediately
      const updatedUser = { ...user, profile_photo: null, profile_verified: false };
      localStorage.setItem('lms_user', JSON.stringify(updatedUser));
      onUpdateUser(updatedUser);
      
      toast.success('Photo removed successfully');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required custom fields
    for (const field of customFields) {
      if (field.required && !customFieldValues[field.id]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    setLoading(true);

    try {
      console.log('Saving profile data:', formData);
      console.log('Saving custom field values:', customFieldValues);
      
      // Update basic profile
      const res = await axios.put(`${API}/users/profile`, formData, { headers: getAuthHeaders() });
      console.log('Profile update response:', res.data);
      
      // Update custom fields (only for fields that have values or have been modified)
      const customFieldPromises = customFields
        .filter(field => customFieldValues[field.id] !== undefined && customFieldValues[field.id] !== '')
        .map(field => {
          console.log(`Updating custom field ${field.label}:`, customFieldValues[field.id]);
          return axios.put(
            `${API}/users/${user.id}/custom-field/${field.id}`,
            { value: customFieldValues[field.id] },
            { headers: getAuthHeaders() }
          );
        });
      
      if (customFieldPromises.length > 0) {
        await Promise.all(customFieldPromises);
        console.log(`✓ Updated ${customFieldPromises.length} custom fields`);
      }
      
      // CRITICAL: Fetch updated user data from server to get latest custom_fields
      const userRes = await axios.get(`${API}/users/${user.id}`, { headers: getAuthHeaders() });
      const updatedUser = userRes.data;
      
      // Update local storage and state with fresh data
      localStorage.setItem('lms_user', JSON.stringify(updatedUser));
      onUpdateUser(updatedUser);
      
      toast.success('Profile updated successfully!');

      if (updatedUser.profile_verified) {
        toast.success('Profile verified! ✓', {
          description: 'Your profile is now complete and verified'
        });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
  };

  const getPhotoUrl = () => {
    if (formData.profile_photo) {
      if (formData.profile_photo.startsWith('http')) {
        return formData.profile_photo;
      }
      return `${BACKEND_URL}${formData.profile_photo}`;
    }
    return null;
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="profile">
      <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 max-w-4xl mx-auto" data-testid="student-profile">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-navy flex items-center space-x-2">
            <span>My Profile</span>
            <VerifiedBadge show={user.profile_verified} size="lg" />
          </h1>
          <p className="text-[#4D4D4D] mt-2 font-light">Manage your personal information</p>
        </div>

        <Card className="border-0 card-shadow bg-white rounded-2xl md:rounded-3xl">
          <CardHeader className="bg-gradient-to-br from-[#F7F7F7] to-white border-b border-[#E7E7E7]">
            <div className="flex items-center space-x-3 md:space-x-4">
              <div className="relative">
                <Avatar className="w-16 h-16 md:w-20 md:h-20 border-4 border-white ring-4 ring-[#E7E7E7]">
                  <AvatarImage src={getPhotoUrl()} />
                  <AvatarFallback className="text-2xl font-bold bg-primary-blue text-white">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl md:text-2xl font-bold text-navy flex items-center space-x-2">
                  <span>{user.first_name} {user.last_name}</span>
                  <VerifiedBadge show={user.profile_verified} />
                </CardTitle>
                <p className="text-[#4D4D4D] mt-1">{user.email}</p>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-3 text-xs md:text-sm">
                  <span className="font-semibold text-primary-blue">GP: {user.xp_points}</span>
                  <span className="font-semibold text-primary-blue">🔥 Streak: {user.daily_streak} days</span>
                  {user.registration_date && (
                    <span className="font-semibold text-[#4D4D4D]">Joined: {new Date(user.registration_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                
                {/* 1. First Name */}
                <div className="space-y-3">
                  <Label htmlFor="first_name" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <User className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>First Name {isFieldLocked('first_name') && '🔒'}</span>
                  </Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    disabled={isFieldLocked('first_name')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="profile-first-name-input"
                  />
                </div>

                {/* 2. Father's Name - Custom Field */}
                {(() => {
                  const fatherNameField = customFields.find(f => f.label === "Father's Name" || f.label === "Father Name");
                  const fieldId = fatherNameField?.id || 'father_name_placeholder';
                  return (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${fieldId}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <User className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Father's Name {isFieldLocked('parent_name') && '🔒'}</span>
                      </Label>
                      <Input
                        id={`custom_${fieldId}`}
                        value={fatherNameField ? (customFieldValues[fatherNameField.id] || '') : ''}
                        onChange={(e) => fatherNameField && handleCustomFieldChange(fatherNameField.id, e.target.value)}
                        disabled={isFieldLocked('parent_name')}
                        placeholder="Enter father's name"
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                  );
                })()}

                {/* 3. Mother's Name - Custom Field */}
                {(() => {
                  const motherNameField = customFields.find(f => f.label === "Mother's Name" || f.label === "Mother Name");
                  const fieldId = motherNameField?.id || 'mother_name_placeholder';
                  return (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${fieldId}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <User className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Mother's Name</span>
                      </Label>
                      <Input
                        id={`custom_${fieldId}`}
                        value={motherNameField ? (customFieldValues[motherNameField.id] || '') : ''}
                        onChange={(e) => motherNameField && handleCustomFieldChange(motherNameField.id, e.target.value)}
                        placeholder="Enter mother's name"
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                      />
                    </div>
                  );
                })()}

                {/* 4. Last Name */}
                <div className="space-y-3">
                  <Label htmlFor="last_name" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <User className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>Last Name {isFieldLocked('last_name') && '🔒'}</span>
                  </Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    disabled={isFieldLocked('last_name')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="profile-last-name-input"
                  />
                </div>

                {/* 5. Father's Mobile Number - Custom Field */}
                {(() => {
                  const fatherMobileField = customFields.find(f => f.label === "Father's Mobile Number" || f.label === "Father Mobile" || f.label === "Father's Mobile");
                  const fieldId = fatherMobileField?.id || 'father_mobile_placeholder';
                  return (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${fieldId}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <Phone className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Father's Mobile Number</span>
                      </Label>
                      <Input
                        id={`custom_${fieldId}`}
                        type="tel"
                        value={fatherMobileField ? (customFieldValues[fatherMobileField.id] || '') : ''}
                        onChange={(e) => fatherMobileField && handleCustomFieldChange(fatherMobileField.id, e.target.value)}
                        placeholder="Enter father's mobile number"
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                      />
                    </div>
                  );
                })()}

                {/* 6. Mother's Mobile Number - Custom Field */}
                {(() => {
                  const motherMobileField = customFields.find(f => f.label === "Mother's Mobile Number" || f.label === "Mother Mobile" || f.label === "Mother's Mobile");
                  const fieldId = motherMobileField?.id || 'mother_mobile_placeholder';
                  return (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${fieldId}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <Phone className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Mother's Mobile Number</span>
                      </Label>
                      <Input
                        id={`custom_${fieldId}`}
                        type="tel"
                        value={motherMobileField ? (customFieldValues[motherMobileField.id] || '') : ''}
                        onChange={(e) => motherMobileField && handleCustomFieldChange(motherMobileField.id, e.target.value)}
                        placeholder="Enter mother's mobile number"
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                      />
                    </div>
                  );
                })()}

                {/* 7. Father's Occupation - Custom Field */}
                {(() => {
                  const fatherOccField = customFields.find(f => f.label === "Father's Occupation" || f.label === "Father Occupation");
                  const fieldId = fatherOccField?.id || 'father_occupation_placeholder';
                  return (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${fieldId}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <User className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Father's Occupation</span>
                      </Label>
                      <Input
                        id={`custom_${fieldId}`}
                        value={fatherOccField ? (customFieldValues[fatherOccField.id] || '') : ''}
                        onChange={(e) => fatherOccField && handleCustomFieldChange(fatherOccField.id, e.target.value)}
                        placeholder="Enter father's occupation"
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                      />
                    </div>
                  );
                })()}

                {/* 8. Mother's Occupation - Custom Field */}
                {(() => {
                  const motherOccField = customFields.find(f => f.label === "Mother's Occupation" || f.label === "Mother Occupation");
                  const fieldId = motherOccField?.id || 'mother_occupation_placeholder';
                  return (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${fieldId}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <User className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Mother's Occupation</span>
                      </Label>
                      <Input
                        id={`custom_${fieldId}`}
                        value={motherOccField ? (customFieldValues[motherOccField.id] || '') : ''}
                        onChange={(e) => motherOccField && handleCustomFieldChange(motherOccField.id, e.target.value)}
                        placeholder="Enter mother's occupation"
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                      />
                    </div>
                  );
                })()}

                {/* 9. Student Mobile Number (Required for verification) */}
                <div className="space-y-3">
                  <Label htmlFor="mobile_number" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <Phone className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>Your Mobile Number {isFieldLocked('phone_number') && '🔒'} <span className="text-red-500">*</span></span>
                  </Label>
                  <Input
                    id="mobile_number"
                    name="mobile_number"
                    type="tel"
                    value={formData.mobile_number}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    required
                    disabled={isFieldLocked('phone_number')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>

                {/* 10. School Name */}
                <div className="space-y-3">
                  <Label htmlFor="school_name" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <School className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>School Name {isFieldLocked('school_name') && '🔒'}</span>
                  </Label>
                  <Input
                    id="school_name"
                    name="school_name"
                    value={formData.school_name}
                    onChange={handleChange}
                    disabled={isFieldLocked('school_name')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="profile-school-input"
                  />
                </div>

                {/* 11. School Board (Required for verification) - FIXED: Added light blue background */}
                <div className="space-y-3">
                  <Label htmlFor="school_type" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <School className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>School Board {isFieldLocked('school_board') && '🔒'} <span className="text-red-500">*</span></span>
                  </Label>
                  <Select 
                    value={formData.school_type || undefined}
                    onValueChange={(value) => handleSelectChange('school_type', value)}
                    disabled={isFieldLocked('school_board')}
                  >
                    <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue bg-white disabled:opacity-60 disabled:cursor-not-allowed">
                      <SelectValue placeholder="Select school board" />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-50 border-2 border-blue-200">
                      <SelectItem value="SSC" className="hover:bg-blue-100 focus:bg-blue-100">SSC</SelectItem>
                      <SelectItem value="CBSE" className="hover:bg-blue-100 focus:bg-blue-100">CBSE</SelectItem>
                      <SelectItem value="ICSE" className="hover:bg-blue-100 focus:bg-blue-100">ICSE</SelectItem>
                      <SelectItem value="IB" className="hover:bg-blue-100 focus:bg-blue-100">IB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* 12. School Board - Custom Field or formData */}
                {!loadingCustomFields && (() => {
                  const schoolBoardField = customFields.find(f => f.label === "School Board");
                  return schoolBoardField ? (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${schoolBoardField.id}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <School className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>School Board</span>
                        {schoolBoardField.required && <span className="text-red-500">*</span>}
                      </Label>
                      {schoolBoardField.field_type === 'dropdown' ? (
                        <Select 
                          value={customFieldValues[schoolBoardField.id] && customFieldValues[schoolBoardField.id] !== '' ? customFieldValues[schoolBoardField.id] : undefined}
                          onValueChange={(value) => handleCustomFieldChange(schoolBoardField.id, value)}
                        >
                          <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue bg-white">
                            <SelectValue placeholder={schoolBoardField.placeholder || 'Select an option'} />
                          </SelectTrigger>
                          <SelectContent className="bg-blue-50 border-2 border-blue-200">
                            {schoolBoardField.options?.map(option => (
                              <SelectItem key={option} value={option} className="hover:bg-blue-100">{option}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={`custom_${schoolBoardField.id}`}
                          value={customFieldValues[schoolBoardField.id] || ''}
                          onChange={(e) => handleCustomFieldChange(schoolBoardField.id, e.target.value)}
                          placeholder={schoolBoardField.placeholder || ''}
                          required={schoolBoardField.required}
                          className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                        />
                      )}
                    </div>
                  ) : null;
                })()}

                {/* 13. Grade & Year */}
                <div className="space-y-3">
                  <Label htmlFor="grade_year" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <School className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>Grade & Year {isFieldLocked('grade_year') && '🔒'} <span className="text-red-500">*</span></span>
                  </Label>
                  <Input
                    id="grade_year"
                    name="grade_year"
                    value={formData.grade_year}
                    onChange={handleChange}
                    placeholder="e.g., 10th Grade, 2024"
                    required
                    disabled={isFieldLocked('grade_year')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="profile-grade-input"
                  />
                </div>

                {/* 14. Date of Birth */}
                <div className="space-y-3">
                  <Label htmlFor="date_of_birth" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <CalendarIcon className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>Date of Birth {isFieldLocked('date_of_birth') && '🔒'} <span className="text-red-500">*</span></span>
                  </Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    required
                    disabled={isFieldLocked('date_of_birth')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>

                {/* 15. City */}
                <div className="space-y-3">
                  <Label htmlFor="city" className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                    <MapPin className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span>City {isFieldLocked('city') && '🔒'}</span>
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={isFieldLocked('city')}
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="profile-city-input"
                  />
                </div>

                {/* 16. State - Custom Field */}
                {!loadingCustomFields && (() => {
                  const stateField = customFields.find(f => f.label === "State");
                  return stateField ? (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${stateField.id}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <MapPin className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>State</span>
                        {stateField.required && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id={`custom_${stateField.id}`}
                        value={customFieldValues[stateField.id] || ''}
                        onChange={(e) => handleCustomFieldChange(stateField.id, e.target.value)}
                        placeholder={stateField.placeholder || ''}
                        required={stateField.required}
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                      />
                    </div>
                  ) : null;
                })()}

                {/* 17. Registration Date - Custom Field (Read-Only) */}
                {(() => {
                  const regDateField = customFields.find(f => f.label === "Registration Date");
                  const regDateValue = customFieldValues[regDateField?.id] || user.registration_date || '';
                  return regDateField && regDateValue ? (
                    <div className="space-y-3">
                      <Label htmlFor={`custom_${regDateField.id}`} className="flex items-center space-x-2 text-[15px] font-semibold text-navy">
                        <CalendarIcon className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                        <span>Registration Date</span>
                      </Label>
                      <Input
                        id={`custom_${regDateField.id}`}
                        value={regDateValue ? new Date(regDateValue).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not available'}
                        readOnly
                        disabled
                        className="h-12 border-2 border-[#E7E7E7] rounded-xl bg-gray-100 text-[#4D4D4D] cursor-not-allowed"
                        title="Registration date is set automatically and cannot be changed"
                      />
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Profile Photo Upload Section */}
              <div className="pt-4 md:pt-6 border-t border-[#E7E7E7]">
                <h3 className="text-lg font-semibold text-navy mb-3 md:mb-4">Profile Photo</h3>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <Avatar className="w-20 h-20 md:w-24 md:h-24 border-4 border-white ring-4 ring-[#E7E7E7] mx-auto sm:mx-0">
                    <AvatarImage src={getPhotoUrl()} />
                    <AvatarFallback className="text-2xl font-bold bg-primary-blue text-white">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2 md:space-y-3 text-center sm:text-left">
                    <p className="text-sm text-[#4D4D4D]">Upload a profile photo to personalize your account. JPG or PNG, max 5MB.</p>
                    <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-primary-blue hover:bg-[#1873CC] text-white px-4 md:px-6 py-2 rounded-xl font-semibold transition-smooth w-full sm:w-auto"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {formData.profile_photo ? 'Change Photo' : 'Upload Photo'}
                      </Button>
                      {formData.profile_photo && (
                        <Button
                          type="button"
                          onClick={handleRemovePhoto}
                          variant="outline"
                          className="border-2 border-red-500 text-red-500 hover:bg-red-50 px-4 md:px-6 py-2 rounded-xl font-semibold transition-smooth w-full sm:w-auto"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Remove Photo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button - Sticky on mobile, regular on desktop */}
              <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-[#E7E7E7] pt-4 pb-4 -mx-6 px-6 mt-6 sm:static sm:bg-transparent sm:border-t-0 sm:pt-4 sm:pb-0 sm:mx-0 sm:px-0 sm:mt-4 z-10 shadow-lg sm:shadow-none">
                <div className="flex justify-center sm:justify-end">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-primary-blue hover:bg-[#1873CC] text-white px-6 md:px-8 py-3 rounded-xl font-semibold transition-smooth disabled:opacity-50 w-full sm:w-auto"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Add bottom padding on mobile to prevent content from being hidden by sticky button */}
      <div className="h-20 sm:hidden"></div>

      {/* Image Crop Modal */}
      <ImageCropModal
        open={showCropModal}
        onClose={() => setShowCropModal(false)}
        imageSrc={imageSrc}
        onSave={handleCropSave}
      />
    </Layout>
  );
}